/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

import {
  isRunningStatus,
} from '@/features/subscription/components/inactive/subscription-lock';
import { inactiveAgencyDestination } from '@/features/subscription/components/inactive/inactive-destinations';
import { getCurrentUserRole, isAdmin } from '@/shared/utils/permissions';
import type { AgencyDashboardItem } from './ManageAgenciesCards';

/** The two piles the workspace sorts the user's offices into. */
export type WorkspaceAgencies = {
  active: AgencyDashboardItem[];
  pending: AgencyDashboardItem[];
};

/**
 * Whether the office is running and therefore belongs in the grid, not in the
 * attention list.
 *
 * The answer comes from the subscription rule the shell already uses, so an
 * office in its free trial cannot be open in one screen and "pending" in
 * another.
 *
 * @param agency - The agency dashboard item to test.
 * @returns True when the office's subscription is running.
 */
export function isActiveAgency(agency: AgencyDashboardItem): boolean {
  return isRunningStatus(agency.status);
}

/**
 * Splits the user's offices into the ones that open and the ones that need
 * something first.
 *
 * The attention list is kept from the people who cannot act on it: a broker
 * cannot choose a plan or replace a card, so an office they merely work in
 * would sit at the top of their workspace asking for something they are not
 * able to do.
 *
 * @param agencies - Every office the user belongs to.
 * @param userEmail - The signed-in user's email, used to read their role.
 * @returns The running offices and the ones waiting on their owner.
 */
export function partitionWorkspaceAgencies(
  agencies: AgencyDashboardItem[],
  userEmail: string | null,
): WorkspaceAgencies {
  const active = agencies.filter(isActiveAgency);
  const pending = agencies.filter(
    (a) => !isActiveAgency(a) && isAdmin(getCurrentUserRole(a, userEmail)),
  );
  return { active, pending };
}

/**
 * Where a workspace card leads.
 *
 * A running office opens; anything else goes to the one screen that explains
 * its state.
 *
 * @param agency - The office whose card was pressed.
 * @returns The path to navigate to.
 */
export function workspaceCardDestination(agency: AgencyDashboardItem): string {
  if (isActiveAgency(agency)) return `/app/agencies/${agency.id}`;
  return inactiveAgencyDestination(agency.id, agency.status);
}
